/**
 * Workflow Manager Page
 *
 * Purpose: Administrative interface for managing workflow definitions and workflow steps
 *          within the OrchestrateHQ AI platform. Supports GLOBAL tenant functionality
 *          and tenant-specific workflow management.
 *
 * Architecture: Uses SkeletonPage template with tabbed interface for different
 *              workflow management functions. Integrates with Auth service for
 *              tenant discovery and permission validation.
 *
 * Dependencies: React, Material-UI, SkeletonPage, MetadataService, AuthContext
 * Author: Development Team
 * Created: 2026-04-13
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Alert, CircularProgress } from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Public as GlobalIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import SkeletonPage from '../../components/layout/SkeletonPage';
import { useAuth } from '../../context/AuthContext';
import WorkflowDefinitionManager from '../../components/workflow/WorkflowDefinitionManager';
import WorkflowStepManager from '../../components/workflow/WorkflowStepManager';
import { metadataService } from '../../services/metadataService';
import TenantSelector from '../../components/common/TenantSelector';
import { DismissibleBanner } from '../../components/common/DismissibleBanner';

// Styled components

const ContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

// Interfaces
interface TenantInfo {
  tenant_id: string;
  name: string;
  is_global: boolean;
  can_edit: boolean;
}

interface MetadataManagerContext {
  current_tenant: TenantInfo;
  available_tenants: TenantInfo[];
  user_permissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workflow-tabpanel-${index}`}
      aria-labelledby={`workflow-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 1, pb: 2 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `workflow-tab-${index}`,
    'aria-controls': `workflow-tabpanel-${index}`,
  };
};

/**
 * Workflow Manager Page Component
 *
 * Purpose: Central interface for workflow management with tenant context switching
 * Logic: Manages tabs, tenant context, and delegating to specific manager components
 */
// Map between tab names (URL) and tab indices
const TAB_NAMES = ['workflow-definitions', 'workflow-steps'] as const;
type TabName = (typeof TAB_NAMES)[number];

const tabNameToIndex = (name: string | null): number => {
  const idx = TAB_NAMES.indexOf(name as TabName);
  return idx >= 0 ? idx : 0;
};

const tabIndexToName = (index: number): TabName => {
  return TAB_NAMES[index] || 'workflow-definitions';
};

const WorkflowManagerPage: React.FC = () => {
  const { state } = useAuth();
  const { user } = state;
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive tab and navigation context from URL params
  const activeTab = tabNameToIndex(searchParams.get('tab'));
  const urlWorkflowId = searchParams.get('workflowId');
  const fromWorkflowLabel = searchParams.get('from');

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<MetadataManagerContext | null>(null);
  const [switchingContext, setSwitchingContext] = useState(false);

  // Breadcrumbs for the page
  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <AdminIcon fontSize="inherit" /> },
    { label: 'Workflow Manager', icon: <AccountTreeIcon fontSize="inherit" /> },
  ];

  /**
   * Load metadata manager context from API
   */
  const loadManagerContext = useCallback(
    async (explicitTenantId?: string, signal?: AbortSignal) => {
      try {
        setLoading(true);
        setError(null);

        // Check if request was cancelled
        if (signal?.aborted) {
          return;
        }

        // Get metadata manager context from API
        const response = await metadataService.getManagerContext(explicitTenantId);

        // Check again if request was cancelled before setting state
        if (!signal?.aborted) {
          if (response.data) {
            setContext(response.data);
          } else {
            throw new Error('No context data received from API');
          }
        }
      } catch (err) {
        // Only handle error if request wasn't cancelled
        if (!signal?.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load workflow manager context');
        }
      } finally {
        // Only set loading to false if request wasn't cancelled
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    []
  );

  // Load initial context - use main tenant context or URL parameter for testing
  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const testTenantId = urlParams.get('tenant_id');

      // Use main tenant context if no URL parameter is provided
      const mainTenantId = testTenantId || user?.activeTenant?.tenant_id;
      await loadManagerContext(mainTenantId, abortController.signal);
    };

    loadData();

    // Cleanup function to abort the request if component unmounts or dependencies change
    return () => {
      abortController.abort();
    };
  }, [loadManagerContext, user?.activeTenant?.tenant_id]);

  /**
   * Handle tenant context switch
   */
  const handleTenantSwitch = async (newTenantId: string) => {
    if (!context || newTenantId === context.current_tenant.tenant_id) {
      return;
    }

    try {
      setSwitchingContext(true);
      setError(null);

      // Switch context using API
      await metadataService.switchContext(newTenantId);

      // Update context locally to maintain the selected tenant
      const newTenant = context.available_tenants.find((t) => t.tenant_id === newTenantId);
      if (newTenant) {
        setContext({
          ...context,
          current_tenant: {
            tenant_id: newTenant.tenant_id,
            name: newTenant.name,
            is_global: newTenant.is_global,
            can_edit: newTenant.can_edit,
          },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch tenant context');
    } finally {
      setSwitchingContext(false);
    }
  };

  /**
   * Handle tab change - updates URL params
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newParams = new URLSearchParams(searchParams);
    const tabName = tabIndexToName(newValue);
    if (tabName === 'workflow-definitions') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tabName);
    }
    // Clear navigation context when manually switching tabs
    newParams.delete('workflowId');
    newParams.delete('from');
    setSearchParams(newParams, { replace: true });
  };

  /**
   * Handle navigation to Workflow Steps tab with a specific workflow selected
   * Includes the workflow definition label for the back banner
   */
  const handleNavigateToSteps = (workflowId: string, workflowLabel?: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'workflow-steps');
    newParams.set('workflowId', workflowId);
    if (workflowLabel) {
      newParams.set('from', workflowLabel);
    }
    setSearchParams(newParams, { replace: false });
  };
  // Loading state
  if (loading) {
    return (
      <SkeletonPage pageTitle="Workflow Manager" breadcrumbs={breadcrumbs}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </SkeletonPage>
    );
  }

  // Error state
  if (error || !context) {
    return (
      <SkeletonPage pageTitle="Workflow Manager" breadcrumbs={breadcrumbs}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Failed to load workflow manager'}
        </Alert>
      </SkeletonPage>
    );
  }

  const { current_tenant, available_tenants, user_permissions } = context;
  const canEdit = current_tenant.is_global
    ? user_permissions.can_edit_global
    : user_permissions.can_edit_tenant;

  return (
    <SkeletonPage pageTitle="Workflow Manager" breadcrumbs={breadcrumbs}>
      {/* Content Header with Tenant Selector */}
      <ContentHeader>
        <Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0 }}>
            Manage workflow definitions and workflow steps
          </Typography>
        </Box>

        {/* Tenant Selector - Top Right */}
        {user_permissions.can_switch_context && (
          <TenantSelector
            currentTenant={current_tenant.tenant_id}
            availableTenants={available_tenants}
            onTenantChange={handleTenantSwitch}
            disabled={switchingContext}
            loading={switchingContext}
            size="small"
            dataTestId="tenant-context-selector"
          />
        )}
      </ContentHeader>

      {/* Global Warning Banner */}
      {current_tenant.is_global && (
        <DismissibleBanner severity="warning" icon={<GlobalIcon />} useGlobalStyle>
          <Typography variant="subtitle1" component="div">
            You are managing GLOBAL workflows
          </Typography>
          <Typography variant="body2">
            Changes to GLOBAL workflows affect all tenants in the system. Please use caution when
            making modifications.
          </Typography>
        </DismissibleBanner>
      )}

      {/* Tab Interface */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="workflow manager tabs">
          <Tab
            label="Workflow Definitions"
            icon={<AccountTreeIcon />}
            iconPosition="start"
            {...a11yProps(0)}
          />
          <Tab
            label="Workflow Steps"
            icon={<AccountTreeIcon />}
            iconPosition="start"
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <WorkflowDefinitionManager
          tenantContext={current_tenant}
          canEdit={canEdit}
          userPermissions={user_permissions}
          onNavigateToSteps={handleNavigateToSteps}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <WorkflowStepManager
          tenantContext={current_tenant}
          canEdit={canEdit}
          userPermissions={user_permissions}
          filterWorkflowId={urlWorkflowId}
          fromWorkflowLabel={fromWorkflowLabel}
        />
      </TabPanel>
    </SkeletonPage>
  );
};

export default WorkflowManagerPage;
